export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  static fromResponse(response: Response, body?: unknown): ApiError {
    let message = `HTTP Error: ${response.status}`;

    if (typeof body === 'object' && body !== null && 'message' in body) {
      const rawMessage = (body as { message: unknown }).message;
      // El backend puede enviar message como string o como array de strings
      if (Array.isArray(rawMessage)) {
        message = rawMessage.join('. ');
      } else if (typeof rawMessage === 'string') {
        message = rawMessage;
      } else {
        message = String(rawMessage);
      }
    }

    const code = typeof body === 'object' && body !== null && 'code' in body
      ? String((body as { code: unknown }).code)
      : undefined;

    // Usar 'error' del response como código si no hay code
    const errorCode = code ?? (typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error: unknown }).error)
      : undefined);

    const details = typeof body === 'object' && body !== null && 'details' in body
      ? ((body as { details: unknown }).details as Record<string, unknown>)
      : undefined;

    return new ApiError(response.status, message, errorCode, details);
  }
}

export const isNetworkError = (error: unknown): boolean => {
  return error instanceof TypeError && error.message === 'Error de red';
};

export const isUnauthorizedError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && error.statusCode === 401;
};

export const isForbiddenError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && error.statusCode === 403;
};

export const isNotFoundError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && error.statusCode === 404;
};

export const isValidationError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && (error.statusCode === 400 || error.statusCode === 422);
};

export const isRateLimitedError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && error.statusCode === 429;
};

export const isConflictError = (error: unknown): boolean => {
  return ApiError.isApiError(error) && error.statusCode === 409;
};
